import { API_ORIGIN } from '../config'
const API_BASE = `${API_ORIGIN}/api`

// Lee el mensaje que manda el servidor (campo message) para mostrarlo tal cual; si no hay, usa el texto por defecto.
async function failWith(response, fallback) {
  let message = fallback
  try {
    const body = await response.json()
    if (body && body.message) message = body.message
  } catch { /* respuesta sin JSON */ }
  const err = new Error(message)
  err.status = response.status
  throw err
}

export async function getChargePeriods(parkId) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/charges/periods`)
  if (!response.ok) throw new Error('Error al obtener los periodos')
  return response.json()
}

export async function getChargeCounts(parkId, period, accumulated) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/charges/counts?period=${period}&accumulated=${accumulated}`)
  if (!response.ok) throw new Error('Error al obtener los contadores')
  return response.json()
}

export async function searchCharges(parkId, query) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/charges/search?q=${encodeURIComponent(query)}`)
  if (!response.ok) throw new Error('Error al buscar')
  return response.json()
}

export async function getCobranzaMetrics(parkId, period, accumulated) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/charges/metrics?period=${period}&accumulated=${accumulated}`)
  if (!response.ok) throw new Error('Error al obtener las métricas')
  return response.json()
}

export async function getAccountsList(parkId, period, accumulated) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/charges/accounts?period=${period}&accumulated=${accumulated}`)
  if (!response.ok) throw new Error('Error al obtener las cuentas')
  return response.json()
}

export async function sendReminder(contractId, recipient, type) {
  const response = await fetch(`${API_BASE}/collections/${contractId}/reminders`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ recipient, type }),
  })
  if (!response.ok) await failWith(response, 'Error al enviar el recordatorio')
  return response.json()
}

// Correos recientes del contrato (estado de cuenta, recordatorios, avisos) con su estado de entrega.
export async function getContractMessages(contractId) {
  const response = await fetch(`${API_BASE}/collections/${contractId}/messages`)
  if (!response.ok) throw new Error('Error al consultar los envíos')
  return response.json()
}

export async function addManagementNote(contractId, text) {
  const response = await fetch(`${API_BASE}/collections/${contractId}/notes`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text, author: 'Usuario' }),
  })
  if (!response.ok) throw new Error('Error al agregar la nota')
  return response.json()
}

export async function getAccountDetail(contractId, period) {
  const response = await fetch(`${API_BASE}/collections/${contractId}/detail?period=${period}`)
  if (!response.ok) throw new Error('Error al obtener el detalle de la cuenta')
  return response.json()
}

export async function registerPayment(contractId, payload) {
  const response = await fetch(`${API_BASE}/collections/${contractId}/payments`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) throw new Error('Error al registrar el pago')
  return response.json()
}

export async function searchTenantsForCharge(parkId, query) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/charges/tenants-search?q=${encodeURIComponent(query)}`)
  if (!response.ok) throw new Error('Error al buscar inquilinos')
  return response.json()
}

export async function getTenantPlots(parkId, tenantId) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/charges/tenants/${tenantId}/plots`)
  if (!response.ok) throw new Error('Error al obtener las naves')
  return response.json()
}

export async function createManualCharge(payload) {
  const response = await fetch(`${API_BASE}/collections/manual-charges`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) throw new Error('Error al crear el cargo')
  return response.json()
}

export async function uploadChargeAttachment(chargeId, file) {
  const formData = new FormData()
  formData.append('file', file)
  const response = await fetch(`${API_BASE}/collections/manual-charges/${chargeId}/attachments`, { method: 'POST', body: formData })
  if (!response.ok) throw new Error('Error al subir el adjunto')
  return response.json()
}

export async function getReminderPreview(contractId, parkId, type, pendingAmount, dueDate, overdueDays) {
  const params = new URLSearchParams({ parkId, type, pendingAmount: pendingAmount || 0, overdueDays: overdueDays || 0 })
  if (dueDate) params.set('dueDate', dueDate)
  const response = await fetch(`${API_BASE}/collections/${contractId}/reminder-preview?${params.toString()}`)
  if (!response.ok) throw new Error('Error al generar el contenido')
  return response.json()
}

export async function sendFullReminder(payload) {
  const response = await fetch(`${API_BASE}/collections/reminders-full`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) await failWith(response, 'Error al enviar el recordatorio')
  return response.json()
}

export async function registerReminderResponse(reminderId, responseText, commitmentDate) {
  const response = await fetch(`${API_BASE}/collections/reminders/${reminderId}/response`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ responseText, commitmentDate }),
  })
  if (!response.ok) throw new Error('Error al registrar la respuesta')
  return response.json()
}

export async function getBillingConfig(parkId) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/billing-config`)
  if (!response.ok) throw new Error('Error al obtener la configuración')
  return response.json()
}
export async function saveBillingConfig(parkId, payload) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/billing-config`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(`Error al guardar la configuración: ${response.status} · ${errorBody}`)
  }
  return response.json()
}

export async function generateStatement(parkId, payload) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/generate-statement`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  })
  if (!response.ok) await failWith(response, 'Error al generar el estado de cuenta')
  return response.json()
}

export async function generateBulkStatements(parkId, payload) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/generate-bulk-statements`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!response.ok) await failWith(response, 'Error al generar los estados de cuenta')
  return response.json()
}
// Desglose de lo cobrado de un mes. basis: 'period' (Cobranza) o 'due' (Finanzas / NOI).
export async function getCollectedBreakdown(parkId, period, basis) {
  const path = basis === 'due' ? 'finance' : 'charges'
  const response = await fetch(`${API_BASE}/parks/${parkId}/${path}/collected-breakdown?period=${period}`)
  if (!response.ok) throw new Error('No se pudo cargar el desglose')
  return response.json()
}

export async function deletePayment(contractId, paymentId) {
  const response = await fetch(`${API_BASE}/collections/${contractId}/payments/${paymentId}`, { method: 'DELETE' })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'No se pudo eliminar el pago')
  }
  return response.json()
}

// Adeudos de contratos terminados o vencidos (no entran en las tarjetas ni la lista de Cobranza).
export async function getFormerDebts(parkId) {
  const response = await fetch(`${API_BASE}/parks/${parkId}/charges/former-debts`)
  if (!response.ok) throw new Error('No se pudieron cargar los adeudos de contratos terminados')
  return response.json()
}

export async function forgiveCharge(chargeId) {
  const response = await fetch(`${API_BASE}/collections/charges/${chargeId}/forgive`, { method: 'POST' })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || 'No se pudo condonar el cargo')
  }
  return response.json()
}